document.addEventListener('DOMContentLoaded', () => {
  const modal = document.getElementById('order-details-modal');
  const modalOverlay = modal?.querySelector('.order-modal__overlay');
  const modalClose = modal?.querySelector('.order-modal__close');
  const pdfButton = document.getElementById('modal-download-pdf');

  if (!modal) return;

  // Read table headers from data attributes on a root element
  const root = document.querySelector('.orders-list-card');
  const headerProductNumber = root?.getAttribute('data-header-product-number') || 'Product ID';
  const headerDescription = root?.getAttribute('data-header-description') || 'Description';
  const headerQtyOrdered = root?.getAttribute('data-header-qty-ordered') || 'Qty Ordered';
  const headerQtyShipped = root?.getAttribute('data-header-qty-shipped') || 'Qty Shipped';
  const headerUnitPrice = root?.getAttribute('data-header-unit-price') || 'Unit Price';
  const headerTotal = root?.getAttribute('data-header-total') || 'Total';
  const headerLineItems = root?.getAttribute('data-header-line-items') || 'Order Line Items';
  const fallbackImage = root?.getAttribute('data-fallback-image') || '';

  // Format money helper
  const formatMoney = (cents, currency = 'USD') => {
    if (!cents) return '$0.00';
    const amount = typeof cents === 'string' ? parseFloat(cents) : cents;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status
      .toString()
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase());
  };

  // Address block helper
  const addressBlock = (title, address, fields) => {
    const addressLines = fields
      .map(f => address[f] ? `<p class="orders-list-order-meta">${address[f]}</p>` : '')
      .filter(Boolean);

    const cityStateZip = [];
    if (address.City) cityStateZip.push(address.City);
    if (address.State) cityStateZip.push(address.State);
    if (address.PostalCode) cityStateZip.push(address.PostalCode);

    return `
            <div class="order-address-block">
                <div class="card">
                    <div class="orders-list-order-title">${title}</div>
                    ${addressLines.join('')}
                    ${cityStateZip.length > 0 ? `<p class="orders-list-order-meta">${cityStateZip.join(', ')}</p>` : ''}
                    ${address.Country ? `<p class="orders-list-order-meta">${address.Country}</p>` : ''}
                </div>
            </div>
        `;
  };

  let currentOrderData = null;

  const buildPdf = () => {
    if (!currentOrderData) return;
    if (!window.jspdf || !window.jspdf.jsPDF) return;

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = 16;

    const addLine = (label, value) => {
      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(label, margin, y);
      doc.setTextColor(20);
      doc.text(String(value || ''), margin + 40, y);
      y += 6;
    };

    const addDivider = () => {
      doc.setLineWidth(0.6);
      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      doc.setLineWidth(0.2);
      y += 6;
    };

    doc.setFontSize(14);
    doc.setTextColor(20);
    doc.text(`Order ${currentOrderData.orderName || ''}`, margin, y);
    y += 8;

    // Totals box (top-right) - mirror modal style
    const totalsRows = Array.from(
      modal?.querySelectorAll('.order-modal__totals .order-totals-row') || []
    );
    const totalsLabels = totalsRows.map(row => {
      const label = row.querySelector('span')?.textContent?.trim() || '';
      return label;
    });

    const subtotalLabel = totalsLabels[0] || 'Subtotal';
    const shippingLabel = totalsLabels[1] || 'Shipping';
    const taxLabel = totalsLabels[2] || 'Tax';
    const totalLabel = totalsLabels[3] || 'Total';

    const boxWidth = 68;
    const boxX = pageWidth - margin - boxWidth;
    const boxY = 8;
    const rowGap = 6;
    const boxHeight = rowGap * 4 + 8;

    doc.setDrawColor(220);
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 2, 2, 'FD');

    const labelX = boxX + 4;
    const valueX = boxX + boxWidth - 4;
    let boxLineY = boxY + 8;

    const drawRow = (label, value, isTotal = false) => {
      doc.setFontSize(isTotal ? 10.5 : 9);
      doc.setTextColor(isTotal ? 20 : 70);
      doc.text(label, labelX, boxLineY);
      doc.setFont(isTotal ? 'helvetica' : 'helvetica', isTotal ? 'bold' : 'normal');
      doc.text(String(value || ''), valueX, boxLineY, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      boxLineY += rowGap;
    };

    drawRow(subtotalLabel, currentOrderData.orderSubtotal || '');
    drawRow(shippingLabel, currentOrderData.orderShippingCharges || '');
    drawRow(taxLabel, currentOrderData.orderTaxAmount || '');
    doc.setDrawColor(220);
    doc.line(labelX, boxLineY - 2, valueX, boxLineY - 2);
    boxLineY += 2;
    drawRow(totalLabel, currentOrderData.orderTotal || '', true);

    addLine('Order Date', currentOrderData.orderDate || '');
    addLine('PO Number', currentOrderData.orderPoNumber || 'N/A');
    addLine('Status', currentOrderData.orderStatus || '');
    addLine('Terms', currentOrderData.orderTerms || '');

    y += 2;
    addDivider();
    const columnGap = 12;
    const columnWidth = (pageWidth - margin * 2 - columnGap) / 2;
    const leftX = margin;
    const rightX = margin + columnWidth + columnGap;
    let leftY = y;
    let rightY = y;

    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text('Shipping Address', leftX, leftY);
    doc.text('Billing Address', rightX, rightY);
    leftY += 6;
    rightY += 6;

    doc.setFontSize(10);
    doc.setTextColor(40);
    (currentOrderData.shippingLines || ['N/A']).forEach(line => {
      const lines = doc.splitTextToSize(line, columnWidth);
      lines.forEach(l => {
        doc.text(l, leftX, leftY);
        leftY += 5;
      });
    });

    (currentOrderData.billingLines || ['N/A']).forEach(line => {
      const lines = doc.splitTextToSize(line, columnWidth);
      lines.forEach(l => {
        doc.text(l, rightX, rightY);
        rightY += 5;
      });
    });

    y = Math.max(leftY, rightY) + 2;
    addDivider();

    y += 2;

    // Shipment table (if present)
    const shipmentList = Array.isArray(currentOrderData.shipments) ? currentOrderData.shipments : [];
    if (shipmentList.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(20);
      doc.text('Shipments', margin, y);
      y += 6;
      const shipmentRows = shipmentList.flatMap(shipment => {
        const shipmentDate = shipment.shipmentDate
          ? new Date(shipment.shipmentDate).toLocaleDateString('en-US')
          : 'N/A';
        const packages = Array.isArray(shipment.packages) ? shipment.packages : [];
        if (packages.length === 0) {
          return [{
            shipmentDate,
            carrier: 'N/A',
            service: 'N/A',
            trackingNumber: 'N/A',
            trackingUrl: '',
            packageNumber: 'N/A'
          }];
        }
        return packages.map(pkg => ({
          shipmentDate,
          carrier: pkg.carrier || 'N/A',
          service: pkg.service || 'N/A',
          trackingNumber: pkg.trackingNumber || 'N/A',
          trackingUrl: pkg.trackingUrl || '',
          packageNumber: pkg.packageNumber || 'N/A'
        }));
      });

      const shipColX = {
        date: margin,
        carrier: margin + 28,
        service: margin + 70,
        tracking: margin + 102,
        package: margin + 152
      };

      const drawShipmentHeader = () => {
        doc.setFontSize(9);
        doc.setTextColor(70);
        doc.text('Ship Date', shipColX.date, y);
        doc.text('Carrier', shipColX.carrier, y);
        doc.text('Service', shipColX.service, y);
        doc.text('Tracking #', shipColX.tracking, y);
        doc.text('Package #', shipColX.package, y);
        y += 3;
        doc.setLineWidth(0.2);
        doc.setDrawColor(230);
        doc.line(margin, y, pageWidth - margin, y);
        y += 4;
        doc.setTextColor(40);
      };

      drawShipmentHeader();
      doc.setFontSize(9);
      shipmentRows.forEach(row => {
        if (y > pageHeight - 16) {
          doc.addPage();
          y = 16;
          drawShipmentHeader();
        }
        doc.text(String(row.shipmentDate), shipColX.date, y);
        doc.text(String(row.carrier), shipColX.carrier, y);
        doc.text(String(row.service), shipColX.service, y);
        if (row.trackingUrl) {
          doc.setTextColor(0, 102, 204);
          doc.textWithLink(String(row.trackingNumber), shipColX.tracking, y, { url: row.trackingUrl });
          doc.setTextColor(40);
        } else {
          doc.text(String(row.trackingNumber), shipColX.tracking, y);
        }
        doc.text(String(row.packageNumber), shipColX.package, y);
        y += 6;
      });

      y += 2;
      addDivider();
    }

    // Items table (no title)
    const colX = {
      description: margin,
      qty: margin + 92,
      unit: margin + 112,
      total: margin + 152
    };
    const rowHeight = 6;

    const drawTableHeader = () => {
      doc.setFontSize(9);
      doc.setTextColor(70);
      doc.text('Description', colX.description, y);
      doc.text('Qty', colX.qty, y, { align: 'right' });
      doc.text('Unit Price', colX.unit, y, { align: 'right' });
      doc.text('Total', colX.total, y, { align: 'right' });
      y += 3;
      doc.setLineWidth(0.2);
      doc.setDrawColor(230);
      doc.line(margin, y, pageWidth - margin, y);
      y += 4;
      doc.setTextColor(40);
    };

    drawTableHeader();
    doc.setFontSize(9);
    (currentOrderData.items || []).forEach(item => {
      if (y > pageHeight - 16) {
        doc.addPage();
        y = 16;
        drawTableHeader();
      }

      const desc = String(item.title || '');
      const descLines = doc.splitTextToSize(desc, colX.qty - margin - 6);
      const lineCount = Math.max(descLines.length, 1);

      descLines.forEach((line, idx) => {
        doc.text(line, colX.description, y + rowHeight * idx);
      });

      doc.text(String(item.qtyOrdered || 0), colX.qty, y, { align: 'right' });
      doc.text(String(item.unitPrice || ''), colX.unit, y, { align: 'right' });
      doc.text(String(item.totalPrice || ''), colX.total, y, { align: 'right' });

      y += rowHeight * lineCount + 2;
    });

    const rawName = currentOrderData.orderName || currentOrderData.orderNumber || 'order';
    const safeName = rawName.toString().replace(/[^\w-]+/g, '_');
    const fileName = `${safeName}.pdf`;
    doc.save(fileName);
  };

  // Open modal with order data
  const openModal = (row) => {
    // Get order data from data attributes
    const orderName = row.getAttribute('data-order-name') || '';
    const orderNumber = row.getAttribute('data-order-number') || 'N/A';
    const orderPoNumber = row.getAttribute('data-order-po-number') || 'N/A';
    const orderShippingCharges = row.getAttribute('data-order-shipping-charges') || '0';
    const orderTaxAmount = row.getAttribute('data-order-tax-amount') || '0';
    const orderTerms = row.getAttribute('data-order-terms') || 'N/A';
    const orderTotal = row.getAttribute('data-order-total') || '0';
    const orderSubtotal = row.getAttribute('data-order-subtotal') || '0';
    const orderCreated = row.getAttribute('data-order-created') || '';
    const orderErpStatus = row.getAttribute('data-erp-status') || '';
    const orderShipmentRaw = row.getAttribute('data-shipment') || '';

    const billingRaw = row.getAttribute('data-billing');
    const shippingRaw = row.getAttribute('data-shipping');
    const erpItemsRaw = row.getAttribute('data-erp-line-items');
    const shopifyItemsRaw = row.getAttribute('data-shopify-line-items');

    // Parse JSON data
    let billing = {}, shipping = {}, items = [], shipments = [];
    
    try {
      billing = billingRaw ? JSON.parse(billingRaw) : {};
      shipping = shippingRaw ? JSON.parse(shippingRaw) : {};
      erpItems = erpItemsRaw ? JSON.parse(erpItemsRaw) : {};
      shopifyItems = shopifyItemsRaw ? JSON.parse(shopifyItemsRaw) : {};
      shipments = orderShipmentRaw ? JSON.parse(orderShipmentRaw) : [];

      if (erpItemsRaw && erpItems.length > 0) {
        const mergedItems = erpItems.map(obj1 => {
          const matchingObj = shopifyItems.find(
            obj2 => obj1.ProductNumber === obj2.PartNumber
              || obj1.ProductNumber === obj2.ProductNumber
          );

          if (!matchingObj) return obj1;
          
          return {
            ...obj1,
            image: matchingObj.image || obj1.image || null,
            partNumber: matchingObj.PartNumber || ''
          };
        });
        
        items = mergedItems ?? [];
      }
      else {
        items = shopifyItemsRaw ? JSON.parse(shopifyItemsRaw) : [];

        items['partNumber'] = matchingObj.PartNumber || '';

        console.log(items);
      }
    } catch (e) {
      console.error('Error parsing order data:', e);
    }

    // Populate modal header
    const modalOrderName = document.getElementById('modal-order-name');
    if (modalOrderName) modalOrderName.textContent = `← ${orderName || 'Order'}`;

    const orderDate = orderCreated ? formatDate(orderCreated) : '';
    const modalOrderDate = document.getElementById('modal-order-date');
    if (modalOrderDate) modalOrderDate.textContent = orderDate;

    const modalOrderStatusText = document.getElementById('modal-order-status-text');
    if (modalOrderStatusText) {
      modalOrderStatusText.textContent = formatStatus(orderErpStatus || 'received');
    }

    const modalPoNumber = document.getElementById('modal-po-number');
    if (modalPoNumber) modalPoNumber.textContent = orderPoNumber || 'N/A';
    const modalTerms = document.getElementById('modal-terms');
    if (modalTerms) modalTerms.textContent = orderTerms || 'N/A';

    const modalStatusBadge = document.getElementById('modal-erp-status-badge');
    if (modalStatusBadge) {
      const statusValue = (orderErpStatus || 'received').toString().toLowerCase();
      const statusLabel = formatStatus(statusValue);
      modalStatusBadge.textContent = statusLabel || 'Received';
      modalStatusBadge.classList.remove('badge--success', 'badge--warning', 'badge--info', 'badge--critical');
      let badgeClass = 'badge--warning';
      if (statusValue === 'shipped') {
        badgeClass = 'badge--success';
      } else if (statusValue === 'accepted') {
        badgeClass = 'badge--info';
      } else if (statusValue === 'cancelled') {
        badgeClass = 'badge--critical';
      }
      modalStatusBadge.classList.add(badgeClass);
    }
    // Populate shipping address
    const shippingAddressContainer = document.getElementById('modal-shipping-address');
    let shippingAddressHtml = 'N/A';
    let shippingLines = [];
    if (shipping.ShipToCompanyName || shipping.ShipToAddress1 || shipping.company || shipping.address1) {
      shippingLines = [];
      if (shipping.ShipToCompanyName || shipping.company) shippingLines.push(shipping.ShipToCompanyName || shipping.company);
      if (shipping.ShipToAddress1 || shipping.address1) shippingLines.push(shipping.ShipToAddress1 || shipping.address1);
      if (shipping.ShipToAddress2 || shipping.address2) shippingLines.push(shipping.ShipToAddress2 || shipping.address2);
      if (shipping.ShipToAddress3) shippingLines.push(shipping.ShipToAddress3);

      const city = shipping.ShipToCity || shipping.city || '';
      const state = shipping.ShipToState || shipping.province || '';
      const zip = shipping.ShipToPostalCode || shipping.zip || '';
      if (city || state || zip) {
        shippingLines.push(`${city}${city && state ? ', ' : ''}${state}${zip ? ' ' + zip : ''}`);
      }

      if (shipping.ShipToCountry || shipping.country) {
        shippingLines.push(shipping.ShipToCountry || shipping.country);
      }

      shippingAddressHtml = shippingLines.map(line => `<p>${line}</p>`).join('');
    }
    if (shippingAddressContainer) shippingAddressContainer.innerHTML = shippingAddressHtml;

    // Populate billing address
    const billingAddressContainer = document.getElementById('modal-billing-address');
    let billingAddressHtml = 'N/A';
    let billingLines = [];
    if (billing.BillToCompanyName || billing.BillToAddress1 || billing.company || billing.address1) {
      billingLines = [];
      if (billing.BillToCompanyName || billing.company) billingLines.push(billing.BillToCompanyName || billing.company);
      if (billing.BillToAddress1 || billing.address1) billingLines.push(billing.BillToAddress1 || billing.address1);
      if (billing.BillToAddress2 || billing.address2) billingLines.push(billing.BillToAddress2 || billing.address2);
      if (billing.BillToAddress3) billingLines.push(billing.BillToAddress3);

      const city = billing.BillToCity || billing.city || '';
      const state = billing.BillToState || billing.province || '';
      const zip = billing.BillToPostalCode || billing.zip || '';
      if (city || state || zip) {
        billingLines.push(`${city}${city && state ? ', ' : ''}${state}${zip ? ' ' + zip : ''}`);
      }

      if (billing.BillToCountry || billing.country) {
        billingLines.push(billing.BillToCountry || billing.country);
      }

      billingAddressHtml = billingLines.map(line => `<p>${line}</p>`).join('');
    }
    if (billingAddressContainer) billingAddressContainer.innerHTML = billingAddressHtml;

    // Populate shipment
    const shipmentContainer = document.getElementById('modal-shipment');
    if (shipmentContainer) {
      if (Array.isArray(shipments) && shipments.length > 0) {
        const rows = shipments.flatMap(shipment => {
          const shipmentDate = shipment.shipmentDate
            ? new Date(shipment.shipmentDate).toLocaleDateString('en-US')
            : 'N/A';
          const packages = Array.isArray(shipment.packages) ? shipment.packages : [];
          if (packages.length === 0) {
            return [{
              shipmentDate,
              carrier: 'N/A',
              service: 'N/A',
              trackingNumber: 'N/A',
              packageNumber: 'N/A'
            }];
          }
          return packages.map(pkg => ({
            shipmentDate,
            carrier: pkg.carrier || 'N/A',
            service: pkg.service || 'N/A',
            trackingNumber: pkg.trackingNumber || 'N/A',
            trackingUrl: pkg.trackingUrl || '',
            packageNumber: pkg.packageNumber || 'N/A'
          }));
        });

        const rowsHtml = rows.map(row => {
          const trackingValue = row.trackingNumber || 'N/A';
          const trackingUrl = (row.trackingUrl || '').toString().trim();
          const trackingHtml = trackingUrl.length > 0
            ? `<a href="${trackingUrl}" target="_blank" rel="noopener noreferrer">${trackingValue}</a>`
            : trackingValue;
          return `
          <tr>
            <td>${row.shipmentDate}</td>
            <td>${row.carrier}</td>
            <td>${row.service}</td>
            <td>${trackingHtml}</td>
            <td>${row.packageNumber}</td>
          </tr>
        `;
        }).join('');

        shipmentContainer.innerHTML = `
          <table class="order-shipment-table">
            <thead>
              <tr>
                <th>Ship Date</th>
                <th>Carrier</th>
                <th>Service</th>
                <th>Tracking #</th>
                <th>Package #</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        `;
      } else {
        shipmentContainer.textContent = 'N/A';
      }
    }

    // Populate line items
    const itemsContainer = document.getElementById('modal-items');
    let itemsHtml = '';

    if (items.length > 0) {
      itemsHtml = `
                <div class="order-items card">
                    <table class="table">
                        <tbody>
                            ${items.map(item => {
        // Handle both custom format (ProductNumber, QtyOrdered, etc.) and Shopify format (title, quantity, etc.)
        const productId = item.ProductNumber || item.sku || item.variant_id || '';
        const title = item.title || item.Description || item.name || '';
        const qtyOrdered = item.QtyOrdered || item.quantity || 0;
        const qtyShipped = item.QtyShipped || item.fulfillable_quantity || item.quantity || 0;
        const unitPrice = item.UnitRegularPrice || item.price || item.original_price || 0;
        const totalPrice = item.TotalPrice || item.final_line_price || item.line_price || 0;
        const placeholderImageUrl = fallbackImage
          || 'https://fprbp1-s0.myshopify.com/cdn/shop/files/coming_20soon_2a5db943-0bfc-46f4-a1a8-ac53ce295edf.png?v=1761662136&width=160';
        const imageCandidate =
          item.image?.url
          || item.image?.src
          || item.product?.featured_image?.src
          || item.product?.featured_image
          || '';
        const imageUrl = (typeof imageCandidate === 'string' && imageCandidate.trim().length > 0)
          ? imageCandidate
          : placeholderImageUrl;
        const partNumber = item.partNumber || '';
        const cpnValue = productId || partNumber;
        const metaLines = [];
        if (cpnValue) metaLines.push(`CPN: ${cpnValue}`);
        const metaHtml = metaLines.length > 0
          ? `<div class="order-item-meta">${metaLines.join('<br>')}</div>`
          : '';

        // Split description to show main title and part number if available
        // const descriptionParts = description.split(' ');
        //const partNumber = productId || (descriptionParts.length > 1 ? descriptionParts[0] : '');
        // const mainDescription = productId ? description : descriptionParts.slice(1).join(' ') || description;

        return `
                                    <tr>
                                        <td>
                                            <div class="order-item-image-block">
                                                <div class="order-item-image">
                                                    ${imageUrl ? `<img src="${imageUrl}" alt="${title}" />` : '<div class="order-item-image__placeholder"></div>'}
                                                    <div class="order-item-qty-badge">${qtyOrdered}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="order-item-title">${title}</div>
                                            ${metaHtml}
                                        </td>
                                        <td class="order-item-mobile-prices-cell">
                                            <div class="order-item-mobile-prices">
                                                <div class="order-item-mobile-price">${formatMoney(unitPrice)}/ea</div>
                                                <div class="order-item-mobile-total">${formatMoney(totalPrice)}</div>
                                            </div>
                                        </td>
                                        <td class="order-item-qty-block">
                                            <div class="order-item-label">Qty Ordered</div>
                                            <div class="order-item-value">${qtyOrdered}</div>
                                        </td>
                                        <td class="order-item-qty-block">
                                            <div class="order-item-label">Qty Shipped</div>
                                            <div class="order-item-value">${qtyShipped}</div>
                                        </td>
                                        <td class="order-item-qty-block order-item-unit">
                                            <div class="order-item-label">${headerUnitPrice}</div>
                                            <div class="order-item-value">${formatMoney(unitPrice)}/ea</div>
                                        </td>
                                        <td class="order-item-qty-block order-item-total">
                                            <div class="order-item-label">${headerTotal}</div>
                                            <div class="order-item-value">${formatMoney(totalPrice)}</div>
                                        </td>
                                    </tr>
                                `;
      }).join('')}
                        </tbody>
                    </table>
                </div>
            `;
    }

    itemsContainer.innerHTML = itemsHtml;

    currentOrderData = {
      orderName,
      orderNumber,
      orderPoNumber,
      orderTerms,
      orderTotal,
      orderSubtotal,
      orderShippingCharges,
      orderTaxAmount,
      orderStatus: formatStatus(orderErpStatus || 'received'),
      orderDate,
      shippingLines: shippingLines.length > 0 ? shippingLines : ['N/A'],
      billingLines: billingLines.length > 0 ? billingLines : ['N/A'],
      shipments,
      items: items.map(item => ({
        title: item.Description || item.title || item.name || '',
        qtyOrdered: item.QtyOrdered || item.quantity || 0,
        unitPrice: formatMoney(item.UnitRegularPrice || item.price || item.original_price || 0),
        totalPrice: formatMoney(item.TotalPrice || item.final_line_price || item.line_price || 0)
      }))
    };

    // Populate totals
    const modalSubtotal = document.getElementById('modal-subtotal');
    const modalShipping = document.getElementById('modal-shipping');
    const modalTax = document.getElementById('modal-tax');
    const modalTotal = document.getElementById('modal-total');

    if (modalSubtotal) modalSubtotal.textContent = orderSubtotal;
    if (modalShipping) {
      modalShipping.textContent = orderShippingCharges;
    }
    if (modalTax) modalTax.textContent = orderTaxAmount;
    if (modalTotal) modalTotal.textContent = orderTotal;

    // Show modal
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  // Event listeners
  document.querySelectorAll('.orders-list-row').forEach(row => {
    row.addEventListener('click', () => {
      openModal(row);
    });
  });

  if (modalClose) {
    modalClose.addEventListener('click', closeModal);
  }

  if (modalOverlay) {
    modalOverlay.addEventListener('click', closeModal);
  }

  if (pdfButton) {
    pdfButton.addEventListener('click', buildPdf);
  }

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });
});
